import {
  addDays,
  addWeeks,
  addMonths,
  addYears,
  isValid,
  parseISO,
  format,
} from 'date-fns';

/**
 * Returns a human-readable date range from two ISO strings.
 * Examples:
 * - ("2025-07-01", "2025-07-15") => "Jul 1 – Jul 15, 2025"
 * - ("2025-07-01", "") => "From Jul 1"
 * - ("", "2025-07-15") => "Until Jul 15"
 */


// ✅ Safe today string in YYYY-MM-DD based on UTC
export const getTodayUTCString = (): string => {
  const now = new Date();
  const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return utcDate.toISOString().split('T')[0];
};

// ✅ Local today string in YYYY-MM-DD based on *local time*
export const getTodayLocalString = (): string => {
  return format(new Date(), 'yyyy-MM-dd');
};


export const formatDateRange = (
  start?: string,
  end?: string
): string | undefined => {
  if (!start && !end) return undefined;

  const startFormatted = start ? format(parseISO(start), 'MMM d') : '';
  const endFormatted = end ? format(parseISO(end), 'MMM d, yyyy') : '';

  if (start && end) return `${startFormatted} – ${endFormatted}`;
  if (start) return `From ${startFormatted}`;
  if (end) return `Until ${endFormatted}`;

  return undefined;
};

/**
 * Converts a local date string (e.g., '2025-07-27') into a UTC ISO string at noon.
 * This avoids timezone shifts when storing dates in Mongo.
 */
export const toUTCNoonDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return utcDate.toISOString();
};

/**
 * Formats a UTC ISO date string into a 'YYYY-MM-DD' string for form <input type="date" />.
 */
//export const formatDateForInput = (isoString: string): string => {
//  return format(parseISO(isoString), 'yyyy-MM-dd');
//};

// New: Accepts Date OR string
export const formatDateForInput = (date: string | Date): string => {
  return format(typeof date === 'string' ? parseISO(date) : date, 'yyyy-MM-dd');
};

// ✅ Displays date safely
export const formatDateForDisplay = (isoDate: string): string => {
  return format(parseISO(isoDate), 'MMM d, yyyy');
};

// ✅ Returns yyyy-MM-dd string at UTC midnight
export const formatUTCDateForCalendar = (dateStr: string): string => {
  const d = new Date(dateStr);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
    .toISOString()
    .split('T')[0];
};


// ✅ utils/dateUtils.ts (or inside component file if temporary)
export const calculateNextDueDate = (
  base: string,
  frequency: string
): string => {
  if (!base || !frequency) return '';

  const baseDate = parseISO(base);
  if (!isValid(baseDate)) return '';

  switch (frequency) {
    case 'daily':
      return formatDateForInput(addDays(baseDate, 1));
    case 'weekly':
      return formatDateForInput(addWeeks(baseDate, 1));
    case 'biweekly':
      return formatDateForInput(addWeeks(baseDate, 2));
    case 'monthly':
      return formatDateForInput(addMonths(baseDate, 1));
    case 'quarterly':
      return formatDateForInput(addMonths(baseDate, 3));
    case 'yearly':
      return formatDateForInput(addYears(baseDate, 1));
    default:
      return '';
  }
};
